/**
 * Contracts Table Component
 *
 * Displays contracts in a table format with sorting and actions
 */

import type { ContractResponse } from '@monorepo/shared/schemas/contracts'
import type { ColumnDef } from '@tanstack/react-table'
import { Eye, MoreHorizontal, Pause, Play, X } from 'lucide-react'
import { useState } from 'react'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { ConfirmationDialog } from '@/shared/components/ui/confirmation-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import type { ContractFilters } from '../hooks/useContracts'
import {
  useActivateContract,
  useCancelContract,
  usePauseContract,
} from '../hooks/useContracts'

interface ContractsTableProps {
  contracts: ContractResponse[]
  onSort: (sortBy: string, sortOrder: 'asc' | 'desc') => void
  onPageChange: (page: number) => void
  onContractClick: (contract: ContractResponse) => void
  filters: ContractFilters
  meta?: {
    total: number
    page: number
    limit: number
    total_pages: number
    has_next: boolean
    has_prev: boolean
  }
}

export function ContractsTable({
  contracts,
  onContractClick,
  meta,
}: ContractsTableProps) {
  const [pauseConfirmContract, setPauseConfirmContract] =
    useState<ContractResponse | null>(null)
  const [cancelConfirmContract, setCancelConfirmContract] =
    useState<ContractResponse | null>(null)
  const [resumeConfirmContract, setResumeConfirmContract] =
    useState<ContractResponse | null>(null)

  const { mutate: activateContract } = useActivateContract()
  const { mutate: pauseContract } = usePauseContract()
  const { mutate: cancelContract } = useCancelContract()

  const getStatusVariant = (status: ContractResponse['status']) => {
    switch (status) {
      case 'active':
        return 'default'
      case 'draft':
      case 'pending_signature':
        return 'secondary'
      case 'suspended':
        return 'outline'
      case 'terminated':
      case 'expired':
        return 'destructive'
      default:
        return 'outline'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const columns: ColumnDef<ContractResponse>[] = [
    {
      accessorKey: 'name',
      header: 'Contract Name',
      cell: ({ row }) => <div className='font-medium'>{row.original.name}</div>,
      size: 250,
    },
    {
      accessorKey: 'customer.name',
      header: 'Customer',
      cell: ({ row }) => (
        <div className='text-sm'>
          <div className='font-medium'>
            {row.original.customer?.name || '-'}
          </div>
          {row.original.customer?.email && (
            <div className='text-muted-foreground text-xs'>
              {row.original.customer.email}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'plan.name',
      header: 'Plan',
      cell: ({ row }) => (
        <div className='text-sm'>
          <div className='font-medium'>{row.original.plan?.name || '-'}</div>
          {row.original.plan?.description && (
            <div className='text-muted-foreground text-xs'>
              {row.original.plan.description}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'billing_interval',
      header: 'Billing',
      cell: ({ row }) => (
        <div className='text-sm'>
          <div className='capitalize'>{row.original.billing_interval}</div>
          <div className='text-muted-foreground text-xs'>
            {row.original.currency}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={getStatusVariant(row.original.status)}>
          {row.original.status
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')}
        </Badge>
      ),
    },
    {
      accessorKey: 'start_date',
      header: 'Start Date',
      cell: ({ row }) => (
        <div className='text-sm'>{formatDate(row.original.start_date)}</div>
      ),
    },
    {
      accessorKey: 'end_date',
      header: 'End Date',
      cell: ({ row }) => (
        <div className='text-sm'>
          {row.original.end_date ? (
            formatDate(row.original.end_date)
          ) : (
            <span className='text-muted-foreground'>Forever</span>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const contract = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' className='h-8 w-8 p-0'>
                <span className='sr-only'>Open menu</span>
                <MoreHorizontal className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onClick={() => onContractClick(contract)}>
                <Eye className='mr-2 h-4 w-4' />
                View Details
              </DropdownMenuItem>

              {contract.status === 'draft' && (
                <DropdownMenuItem onClick={() => activateContract(contract.id)}>
                  <Play className='mr-2 h-4 w-4' />
                  Activate Contract
                </DropdownMenuItem>
              )}

              {contract.status === 'active' && (
                <>
                  <DropdownMenuItem
                    onClick={() => setPauseConfirmContract(contract)}
                  >
                    <Pause className='mr-2 h-4 w-4' />
                    Pause Contract
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setCancelConfirmContract(contract)}
                  >
                    <X className='mr-2 h-4 w-4' />
                    Cancel Contract
                  </DropdownMenuItem>
                </>
              )}

              {contract.status === 'suspended' && (
                <DropdownMenuItem
                  onClick={() => setResumeConfirmContract(contract)}
                >
                  <Play className='mr-2 h-4 w-4' />
                  Resume Contract
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      size: 50,
    },
  ]

  const handlePauseConfirm = () => {
    if (pauseConfirmContract) {
      pauseContract(pauseConfirmContract.id)
      setPauseConfirmContract(null)
    }
  }

  const handleCancelConfirm = () => {
    if (cancelConfirmContract) {
      cancelContract({
        id: cancelConfirmContract.id,
        reason: 'User requested cancellation',
      })
      setCancelConfirmContract(null)
    }
  }

  const handleResumeConfirm = () => {
    if (resumeConfirmContract) {
      activateContract(resumeConfirmContract.id)
      setResumeConfirmContract(null)
    }
  }

  return (
    <div className='space-y-4'>
      {/* Enhanced Contracts Table */}
      <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50 [&_tbody_tr]:cursor-pointer'>
        <EnhancedTable
          data={contracts}
          columns={columns}
          height='600px'
          className='bg-transparent border-0'
          onRowClick={onContractClick}
        />
      </div>

      {/* Pagination */}
      {meta && meta.total_pages > 1 && (
        <div className='flex items-center justify-between'>
          <div className='text-sm text-muted-foreground'>
            Showing {(meta.page - 1) * meta.limit + 1} to{' '}
            {Math.min(meta.page * meta.limit, meta.total)} of {meta.total}{' '}
            contracts
          </div>
        </div>
      )}

      {/* Pause Confirmation Dialog */}
      <ConfirmationDialog
        open={!!pauseConfirmContract}
        onOpenChange={() => setPauseConfirmContract(null)}
        title='Pause Contract'
        description={`Are you sure you want to pause the contract "${pauseConfirmContract?.name}"? The contract will be suspended and billing will be paused until resumed.`}
        confirmText='Pause Contract'
        onConfirm={handlePauseConfirm}
      />

      {/* Cancel Confirmation Dialog */}
      <ConfirmationDialog
        open={!!cancelConfirmContract}
        onOpenChange={() => setCancelConfirmContract(null)}
        title='Cancel Contract'
        description={`Are you sure you want to cancel the contract "${cancelConfirmContract?.name}"? This action cannot be undone and will permanently cancel the contract.`}
        confirmText='Cancel Contract'
        onConfirm={handleCancelConfirm}
      />

      {/* Resume Confirmation Dialog */}
      <ConfirmationDialog
        open={!!resumeConfirmContract}
        onOpenChange={() => setResumeConfirmContract(null)}
        title='Resume Contract'
        description={`Are you sure you want to resume the contract "${resumeConfirmContract?.name}"? Billing will resume according to the contract terms.`}
        confirmText='Resume Contract'
        onConfirm={handleResumeConfirm}
      />
    </div>
  )
}
